import React, { FC, useState } from 'react';
import css from './ContactRepScreen.scss';
import factoringLogoBlue from '../../assets/images/factoring-logo-blue.svg';
import profileIcon from '../../assets/images/profile.svg';
import {
  CircularProgress,
  DriftSnackbar,
  FormControl,
  FormHelperText,
  InputLabel,
  KeyboardArrowLeftIcon,
  MenuItem,
  MultiLineTextField,
  Select,
  Button,
  styled,
} from 'drift-ui';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import salesforceService from '../../services/salesforceService';
import { RootState } from '../../store';
import { setDisplayContactRepScreen, setContactRepSnackbar } from '../../store/contactRepForm';

const StyledMultiLine = styled(MultiLineTextField)({
  '& .MuiInputBase-root': {
    fontSize: '16px',
  },
  '& .MuiFormLabel-root': {
    fontSize: '16px',
  },
  '& .MuiFormHelperText-root': {
    fontSize: '14px',
  },
});

type ContactRepScreenProps = {
  onClose: () => void;
};

export const ContactRepScreen: FC<ContactRepScreenProps> = ({ onClose }) => {
  const [casePayload, setCasePayload] = useState({
    caseReason: '',
    caseType: 'Factoring',
    description: '',
    origin: 'RTS Pro Web',
  });
  const [errorText, setErrorText] = useState({
    caseReason: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const contactRepSnackbar = useSelector((state: RootState) => state.contactRep.contactRepSnackbar);
  const email = useSelector((state: RootState) => state.adminShell.user.email);

  const submitCasePayload = async () => {
    const isValid = Object.keys(errorText).map((key) => {
      const isValuePresent = casePayload[key as keyof typeof casePayload];
      setErrorText((prev) => ({
        ...prev,
        [key]: isValuePresent ? '' : t('required_contact_screen'),
      }));
      return !!isValuePresent;
    });
    if (isValid.some((v) => !v)) return;
    try {
      setLoading(true);
      await salesforceService.createSalesforceCase(casePayload);
      dispatch(setDisplayContactRepScreen(false));
      dispatch(setContactRepSnackbar('success'));
    } catch (e) {
      console.log('Error creating salesforce case: ', e);
      dispatch(setContactRepSnackbar('error'));
    } finally {
      setLoading(false);
    }
  };

  const updateCasePayload = (key: string, value: string) => {
    setCasePayload((prev) => ({ ...prev, [key]: value }));
    setErrorText((prev) => ({ ...prev, [key]: '' }));
  };

  return (
    <>
      <div className={css.container}>
        <div className={css.contactRepContainer}>
          <Button
            className={css.backButton}
            onClick={onClose}
            disabled={loading}
            startIcon={<KeyboardArrowLeftIcon className={cx(css.backIcon, loading && css.backIconDisabled)} />}
            variant="text"
          >
            <p className={css.backText}>{t('back')}</p>
          </Button>
          <LoadingWrapper loading={loading}>
            <div className={css.contactRepHeader}>
              <img className={css.headerImage} src={factoringLogoBlue} alt="Factoring Logo" />
              <div className={css.text}>{t('contact_factoring')}</div>
            </div>
            <div className={css.separator} />
            <div className={css.profile}>
              <img className={css.profileImage} src={profileIcon} alt="Profile Icon" />
              <div className={css.text}>{email}</div>
            </div>
            <FormControl fullWidth>
              <InputLabel error={!!errorText.caseReason} id="reason-suffix-label" className={css.reasonLabel}>
                {t('what_can_we_help_you_with')}
              </InputLabel>
              <Select
                labelId="reason-suffix-label"
                label={t('what_can_we_help_you_with')}
                onChange={(e) => updateCasePayload('caseReason', e.target.value as string)}
                value={casePayload.caseReason}
                error={!!errorText.caseReason}
                className={css.reasonSelect}
              >
                <MenuItem className={css.reasonMenuItem} value="Purchase Update/Held Invoice">
                  {t('purchase_update_held_invoice')}
                </MenuItem>
                <MenuItem className={css.reasonMenuItem} value="Reserve Inquiry">
                  {t('reserve_inquiry')}
                </MenuItem>
                <MenuItem className={css.reasonMenuItem} value="Bank Change Request">
                  {t('bank_change_request')}
                </MenuItem>
                <MenuItem className={css.reasonMenuItem} value="Letter of Release Request">
                  {t('letter_of_release_request')}
                </MenuItem>
                <MenuItem className={css.reasonMenuItem} value="Other">
                  {t('other')}
                </MenuItem>
              </Select>
              <FormHelperText error={!!errorText.caseReason} className={css.reasonHelperText}>
                {errorText.caseReason}
              </FormHelperText>
            </FormControl>
            <div className={css.description}>
              <StyledMultiLine
                label={t('request_details')}
                value={casePayload.description}
                className={css.multilineTextInput}
                onChange={(e) => updateCasePayload('description', e.target.value as string)}
                rows={5}
                inputProps={{ maxLength: 500 }}
                fullWidth
                error={!!errorText.description}
                helperText={errorText.description ? errorText.description : `${casePayload.description.length}/500`}
              />
            </div>
            <Button
              className={css.submitButton}
              variant="contained"
              color="primary"
              onClick={submitCasePayload}
              disabled={loading}
            >
              <p className={css.buttonContainer}>{t('submit')}</p>
            </Button>
          </LoadingWrapper>
        </div>
      </div>
      <div className={css.snackBar}>
        <DriftSnackbar
          open={contactRepSnackbar === 'error'}
          onClose={() => dispatch(setContactRepSnackbar(undefined))}
          message={t('contact_rep_error')}
          autoHideDuration={3000}
          variant="error"
        />
      </div>
    </>
  );
};

const LoadingWrapper: FC<{ loading: boolean; children: React.ReactNode }> = ({ loading, children }) => {
  if (loading)
    return (
      <div className={css.loadingContainer}>
        <CircularProgress size={140} />
      </div>
    );
  return <>{children}</>;
};
